import { JSX, useState } from "react";

import MuiDrawer from "@mui/material/Drawer";
import { styled, type Theme, type CSSObject, useTheme } from "@mui/material/styles";

import SidebarMenu from "./SidebarContent";

const drawerWidth = 250;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  borderTop: 0,
  borderBottom: 0,
  borderLeft: 0,
  borderRadius: 0,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderTop: 0,
  borderBottom: 0,
  borderLeft: 0,
  borderRadius: 0,
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

const getStyles = (theme: Theme) => ({
  drawer: {
    border: "none",
    "& .MuiDrawer-paper": {
      border: "none",
      borderRight: `1px solid ${theme.palette.border.default}`,
    },
  },
});

function Sidebar(): JSX.Element {
  const [open] = useState(true);
  const theme = useTheme();

  return (
    <Drawer variant='permanent' open={open} sx={getStyles(theme).drawer}>
      <SidebarMenu open={open} />
    </Drawer>
  );
}

export default Sidebar;
